import { useMemo } from "react";
import { Button, Group, Menu, ThemeIcon } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconCheck, IconChevronDown, IconX } from "@tabler/icons-react";
import { get } from "lodash";
import moment from "moment";

import { ListWorkOrderDto, WorkOrderStatus } from "../models/workorder.models";
import { getWorkOrderStatusDescription } from "./workorder.functions";
import { useGetPrioritiesQuery } from "../api/priority.api";
import { useGetLocationsQuery } from "../api/location.api";
import { useGetCategoriesQuery } from "../api/category.api";
import { useGetUsersQuery } from "../api/user.api";
import { useGetTeamsQuery } from "../api/team.api";
import { useSearch } from "./SearchBox.hook";

export function useFilteredWorkOrders(workOrders: ListWorkOrderDto[]) {
	const { query } = useSearch();
	const { statusFilter, priorityFilter, locationFilter, categoryFilter, assignedToFilter, startDateFilter, dueDateFilter } = useWorkOrderFilters();

	const filteredWorkOrders = useMemo(() => {
		let filtered = workOrders ?? [];

		if (query && query.length > 0) {
			const lowerQuery = query.toLowerCase();
			filtered = workOrders?.filter(wo => 
				(wo.WorkOrderNumber && wo.WorkOrderNumber.toLowerCase().includes(lowerQuery)) ||
				(wo.Title && wo.Title.toLowerCase().includes(lowerQuery))) || [];
		}

		if (statusFilter >= 0) {
			filtered = filtered.filter(wo => wo.Status == statusFilter);
		}

		if (priorityFilter) {
			filtered = filtered.filter(wo => wo.Priority.PriorityId === priorityFilter);
		}

		if (locationFilter) {
			filtered = filtered.filter(wo => wo.Location.LocationId === locationFilter);
		}

		if (categoryFilter) {
			filtered = filtered.filter(wo => wo.Categories.some(cat => cat.CategoryId === categoryFilter));
		}

		if (assignedToFilter) {
			filtered = filtered.filter(wo => wo.AssignedTo?.Users.some(user => user.UserId === assignedToFilter) ||
				wo.AssignedTo?.Teams.some(team => team.TeamId === assignedToFilter));
		}

		if (startDateFilter) {
			if (startDateFilter === 'start-today') {
				filtered = filtered.filter(wo => wo.StartedDate && moment(wo.StartedDate).isBetween(moment().startOf('day'), moment().endOf('day'), 'day', '[]'));
			} else if (startDateFilter === 'start-yesterday') {
				filtered = filtered.filter(wo => wo.StartedDate && moment(wo.StartedDate).isBetween(moment().subtract(1, 'day').startOf('day'), moment().subtract(1, 'day').endOf('day'), 'day', '[]'));
			} else if (startDateFilter === 'start-last-7-days') {
				filtered = filtered.filter(wo => wo.StartedDate && moment(wo.StartedDate).isBetween(moment().subtract(7, 'days').startOf('day'), moment().subtract(7, 'days').endOf('day'), 'day', '[]'));
			} else if (startDateFilter === 'start-last-30-days') {
				filtered = filtered.filter(wo => wo.StartedDate && moment(wo.StartedDate).isBetween(moment().subtract(30, 'days').startOf('day'), moment().subtract(30, 'days').endOf('day'), 'day', '[]'));
			} else if (startDateFilter === 'no-start-date') {
				filtered = filtered.filter(wo => !wo.StartedDate);
			} else if (startDateFilter === 'has-start-date') {
				filtered = filtered.filter(wo => wo.StartedDate);
			}
		}

		if (dueDateFilter) {
			if (dueDateFilter === 'due-today') {
				filtered = filtered.filter(wo => wo.DueDate && moment(wo.DueDate).isBetween(moment().startOf('day'), moment().endOf('day'), 'day', '[]') && wo.Status !== WorkOrderStatus.Completed && wo.Status !== WorkOrderStatus.Archived);
			} else if (dueDateFilter === 'due-tomorrow') {
				filtered = filtered.filter(wo => wo.DueDate && moment(wo.DueDate).isBetween(moment().add(1, 'day').startOf('day'), moment().add(1, 'day').endOf('day'), 'day', '[]') && wo.Status !== WorkOrderStatus.Completed && wo.Status !== WorkOrderStatus.Archived);
			} else if (dueDateFilter === 'due-next-7-days') {
				filtered = filtered.filter(wo => wo.DueDate && moment(wo.DueDate).isBetween(moment().startOf('day'), moment().add(7, 'days').endOf('day'), 'day', '[]') && wo.Status !== WorkOrderStatus.Completed && wo.Status !== WorkOrderStatus.Archived);
			} else if (dueDateFilter === 'due-next-30-days') {
				filtered = filtered.filter(wo => wo.DueDate && moment(wo.DueDate).isBetween(moment(), moment().add(1, "month"), 'day', '[]') && wo.Status !== WorkOrderStatus.Completed && wo.Status !== WorkOrderStatus.Archived);
			} else if (dueDateFilter === 'overdue') {
				filtered = filtered.filter(wo => wo.DueDate && moment(wo.DueDate).isBefore(moment(), 'day') && wo.Status !== WorkOrderStatus.Completed && wo.Status !== WorkOrderStatus.Archived);
			} else if (dueDateFilter === 'no-due-date') {
				filtered = filtered.filter(wo => !wo.DueDate);
			} else if (dueDateFilter === 'has-due-date') {
				filtered = filtered.filter(wo => wo.DueDate);
			}
		}

		return filtered;
	}, [query, workOrders, statusFilter, priorityFilter, locationFilter, categoryFilter]);

	const filterNoResults = (!query || query.length === 0) && filteredWorkOrders.length === 0 && (statusFilter >= 0 || priorityFilter !== '' || locationFilter !== '' || categoryFilter !== '' || assignedToFilter !== '' || startDateFilter !== '' || dueDateFilter !== '');
	const searchNoResults = query && query.length > 0 && filteredWorkOrders.length === 0;
	const noData = workOrders.length === 0;

	return { 
		filteredWorkOrders, 
		filterNoResults, 
		searchNoResults,
		noData: noData && !filterNoResults && !searchNoResults
	};
}

const StatusFilter = ({ value, onChange }: { value: WorkOrderStatus; onChange: (value: WorkOrderStatus) => void; }) => {
	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return <>
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg">
			<Menu.Target>
				<Button variant={ value === -1 ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === -1 && "Status"}
					{value !== -1 && getWorkOrderStatusDescription(value)}
				</Button>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Status
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange(-1)} rightSection={value === -1 ? checkIcon : null}>All</Menu.Item>
				<Menu.Item onClick={() => onChange(WorkOrderStatus.Open)} rightSection={value === WorkOrderStatus.Open ? checkIcon : null}>Open</Menu.Item>
				<Menu.Item onClick={() => onChange(WorkOrderStatus.InProgress)} rightSection={value === WorkOrderStatus.InProgress ? checkIcon : null}>In Progress</Menu.Item>
				<Menu.Item onClick={() => onChange(WorkOrderStatus.OnHold)} rightSection={value === WorkOrderStatus.OnHold ? checkIcon : null}>On Hold</Menu.Item>
				<Menu.Item onClick={() => onChange(WorkOrderStatus.Completed)} rightSection={value === WorkOrderStatus.Completed ? checkIcon : null}>Completed</Menu.Item>
				<Menu.Item onClick={() => onChange(WorkOrderStatus.Archived)} rightSection={value === WorkOrderStatus.Archived ? checkIcon : null}>Archived</Menu.Item>
			</Menu.Dropdown>
		</Menu>
	</>
}

const PriorityFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void; }) => {
	const { data: priorities } = useGetPrioritiesQuery();

	const priorityOptions = useMemo(() => {
		return priorities?.map(priority => ({
			label: get(priority, 'Title', ''),
			value: get(priority, 'PriorityId', '')
		})) || [];
	}, [priorities]);

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return <>
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg">
			<Menu.Target>
				<Button variant={ value === "" ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === "" && "Priority"}
					{value !== "" && priorities?.find(option => option.PriorityId === value)?.ShortTitle}
				</Button>
			</Menu.Target>
			<Menu.Dropdown>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Priority
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item>
				{priorityOptions.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	</>
}

const LocationFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void; }) => {
	const { data: locations } = useGetLocationsQuery();
	const locationOptions = useMemo(() => {
		return locations?.map(location => ({
			label: location.Title,
			value: location.LocationId
		})) || [];
	}, [locations]);

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return <>
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg" width={170}>
			<Menu.Target>
				<Button variant={value === "" ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === "" && "Location"}
					{value !== "" && locationOptions.find(option => option.value === value)?.label}
				</Button>
			</Menu.Target>
			<Menu.Dropdown mah={350} w={200} style={{ overflowY: 'auto' }}>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Location
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item>
				{locationOptions.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	</>
}

const CategoryFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void; }) => {
	const { data: categories } = useGetCategoriesQuery();
	const categoryOptions = useMemo(() => {
		return categories?.map(category => ({
			label: category.Title,
			value: category.CategoryId
		})) || [];
	}, [categories]);

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return (
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg" width={170}>
			<Menu.Target>
				<Button variant={value === "" ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === "" && "Category"}
					{value !== "" && categoryOptions.find(option => option.value === value)?.label}
				</Button>
			</Menu.Target>
			<Menu.Dropdown mah={350} w={200} style={{ overflowY: 'auto' }}>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Category
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item>
				{categoryOptions.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	);
};

const AssignedToFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void; }) => {
	const { data: users } = useGetUsersQuery();
	const { data: teams } = useGetTeamsQuery();

	const userOptions = useMemo(() => {
		return users?.map(user => ({
			label: user.Name,
			value: user.UserId
		})) || [];
	}, [users]);

	const teamOptions = useMemo(() => {
		return teams?.map(team => ({
			label: team.Name,
			value: team.TeamId
		})) || [];
	}, [teams]);

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return (
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg" width={170}>
			<Menu.Target>
				<Button variant={value === "" ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === "" && "Assigned To"}
					{value !== "" && value.startsWith('user_') && userOptions.find(option => option.value === value)?.label}
					{value !== "" && value.startsWith('team_') && teamOptions.find(option => option.value === value)?.label}
				</Button>
			</Menu.Target>
			<Menu.Dropdown mah={350} w={200} style={{ overflowY: 'auto' }}>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by User
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item>
				{userOptions.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
				<Menu.Divider />
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Team
					</Group>
				</Menu.Label>
				{/* <Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item> */}
				{teamOptions.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	);
}

const StartDateFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void; }) => {
	const options = [
		{ label: 'Today', value: 'start-today' },
		{ label: 'Yesterday', value: 'start-yesterday' },
		{ label: 'Last 7 Days', value: 'start-last-7-days' },
		{ label: 'Last 30 Days', value: 'start-last-30-days' },
		{ label: 'No Start Date', value: 'no-start-date' },
		{ label: 'Has Start Date', value: 'has-start-date' }
	];

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return (
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg">
			<Menu.Target>
				<Button variant={value === "" ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === "" && "Start Date"}
					{value !== "" && options.find(option => option.value === value)?.label}
				</Button>
			</Menu.Target>
			<Menu.Dropdown mah={350} w={200} style={{ overflowY: 'auto' }}>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Start Date
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item>
				{options.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	);
}
const DueDateFilter = ({ value, onChange }: { value: string; onChange: (value: string) => void; }) => {
	const options = [
		{ label: 'Due Today', value: 'due-today' },
		{ label: 'Due Tomorrow', value: 'due-tomorrow' },
		{ label: 'Due Next 7 Days', value: 'due-next-7-days' },
		{ label: 'Due Next 30 Days', value: 'due-next-30-days' },
		{ label: 'Overdue', value: 'overdue' },
		{ label: 'No Due Date', value: 'no-due-date' },
		{ label: 'Has Due Date', value: 'has-due-date' }
	];

	const checkIcon = <ThemeIcon radius="xl" variant="filled" size={16}><IconCheck size={12} stroke={3} /></ThemeIcon>;

	return (
		<Menu position="bottom-start" radius="lg" closeOnItemClick={false} closeOnClickOutside={true} offset={4} shadow="lg">
			<Menu.Target>
				<Button variant={value === "" ? "light" : "filled"} size="compact-sm" radius="md" rightSection={<IconChevronDown size={14} />}>
					{value === "" && "Due Date"}
					{value !== "" && options.find(option => option.value === value)?.label}
				</Button>
			</Menu.Target>
			<Menu.Dropdown mah={350} w={200} style={{ overflowY: 'auto' }}>
				<Menu.Label>
					<Group justify="space-between" wrap="nowrap">
						Filter by Due Date
						{/* <ActionIcon size={22} color="" variant="subtle" radius="xl" mr={-7} onClick={() => { }}>
							<IconCircleX size={22} />
						</ActionIcon> */}
					</Group>
				</Menu.Label>
				<Menu.Item onClick={() => onChange('')} rightSection={value !== '' ? null : checkIcon}>All</Menu.Item>
				{options.map(option => (
					<Menu.Item key={option.value} onClick={() => onChange(option.value)} rightSection={value === option.value ? checkIcon : null}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	);
}

export const useWorkOrderFilters = () => {
	const [statusFilter, setStatusFilter] = useLocalStorage<number>({
		key: `work-order-status-filter`,
		defaultValue: -1
	});

	const [priorityFilter, setPriorityFilter] = useLocalStorage<string>({
		key: `work-order-priority-filter`,
		defaultValue: ''
	});

	const [locationFilter, setLocationFilter] = useLocalStorage<string>({
		key: `work-order-location-filter`,
		defaultValue: ''
	});

	const [categoryFilter, setCategoryFilter] = useLocalStorage<string>({
		key: `work-order-category-filter`,
		defaultValue: ''
	});

	const [assignedToFilter, setAssignedToFilter] = useLocalStorage<string>({
		key: `work-order-assigned-to-filter`,
		defaultValue: ''
	});

	const [startDateFilter, setStartDateFilter] = useLocalStorage<string>({
		key: `work-order-start-date-filter`,
		defaultValue: ''
	});

	const [dueDateFilter, setDueDateFilter] = useLocalStorage<string>({
		key: `work-order-due-date-filter`,
		defaultValue: ''
	});

	const showResetFilters = useMemo(() => {
		return statusFilter !== -1 || priorityFilter !== '' || locationFilter !== '' || categoryFilter !== '' || assignedToFilter !== '' || startDateFilter !== '' || dueDateFilter !== '';
	}, [statusFilter, priorityFilter, locationFilter, categoryFilter, assignedToFilter, startDateFilter, dueDateFilter]);

	const handleResetFilters = () => {
		setStatusFilter(-1);
		setPriorityFilter('');
		setLocationFilter('');
		setCategoryFilter('');
		setAssignedToFilter('');
		setStartDateFilter('');
		setDueDateFilter('');
	}

	const FilterBar = useMemo(() => {
		return <>
			<Group mt="sm" mb={-20} gap={5}>
				<StatusFilter value={statusFilter} onChange={setStatusFilter} />
				<PriorityFilter value={priorityFilter} onChange={setPriorityFilter} />
				<LocationFilter value={locationFilter} onChange={setLocationFilter} />
				<CategoryFilter value={categoryFilter} onChange={setCategoryFilter} />
				<AssignedToFilter value={assignedToFilter} onChange={setAssignedToFilter} />
				<StartDateFilter value={startDateFilter} onChange={setStartDateFilter} />
				<DueDateFilter value={dueDateFilter} onChange={setDueDateFilter} />
				{showResetFilters &&
				<Button variant="light" size="compact-sm" rightSection={<IconX size={14} />} radius="md" onClick={handleResetFilters}>
					Reset filters
				</Button>
				}
			</Group>
		</>;
	}, [statusFilter, priorityFilter, locationFilter, categoryFilter, assignedToFilter, startDateFilter, dueDateFilter]);

	return { FilterBar, statusFilter, priorityFilter, locationFilter, categoryFilter, assignedToFilter, startDateFilter, dueDateFilter, resetFilters: handleResetFilters };
}